import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useMemo } from 'react';
import { ServerTable } from 'shared/ui/ServerTable';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { useGetCurrencySymbol } from 'app/appState';
import { SizeCodeType } from 'entities/SizeCode';
import { ExportEntity, ExportModal, getBoxesRevenueColumnKeys, useOpenExportModal } from 'features/ExportModal';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import { BoxRevenueStatisticsObject, useGetBoxesRevenueStatisticsQuery } from 'entities/Warehouse';
import { useTableFilterContext } from 'features/TableFilter';
import { ServerTableActions } from 'shared/ui/ServerTable/types';
import { transformBoxesRevenueFilters } from '../utils/transformBoxesRevenueFilters';
import { BoxesRevenueFilter } from './BoxesRevenueFilter';

export const BoxesRevenueTable: FC = memo(() => {
  const currencySymbol = useGetCurrencySymbol();

  const { openExportModal, isExportEnabled } = useOpenExportModal();

  const { t } = useAppTranslation(['contracts', 'common']);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const columns = useMemo<ColumnsType<BoxRevenueStatisticsObject>>(
    () => [
      {
        title: t('Warehouse'),
        key: 'warehouseName',
        render: (_, record) => <div>{record.warehouseName}</div>,
      },
      {
        title: t('Box'),
        key: 'name',
        render: (_, record) => <div className="font-semibold">{record.name}</div>,
      },
      {
        title: t('Square'),
        key: 'square',
        render: (_, record) => (
          <div className="flex flex-col">
            <div className="font-bold">
              {record.square}m<sup className="font-bold">{record.sizeCodeType === SizeCodeType.CUBIC_BOX ? 3 : 2}</sup>
            </div>
          </div>
        ),
      },
      {
        title: t('Occupied days'),
        key: 'totalOccupiedDays',
        render: (_, record) => (
          <div className="font-semibold text-accent">
            {record.totalOccupiedDays} {t('days')}
          </div>
        ),
      },
      {
        title: t('Unoccupied days'),
        key: 'totalUnoccupiedDays',
        render: (_, record) => (
          <div className="font-semibold text-error">
            {record.totalUnoccupiedDays} {t('days')}
          </div>
        ),
      },
      {
        title: t('Average occupancy'),
        key: 'averageOccupancyPercent',
        render: (_, record) => <div className="font-semibold text-accent">{record.averageOccupancyPercent}%</div>,
      },
      {
        title: t('Total revenue'),
        key: 'totalRevenue',
        render: (_, record) => (
          <div className="font-semibold text-accent">
            {record.totalRevenue} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Revenue without discount'),
        key: 'totalRevenueWithoutDiscount',
        render: (_, record) => (
          <div className="font-semibold text-accent">
            {record.totalRevenueWithoutDiscount} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Discount amount'),
        key: 'totalDiscountAmount',
        render: (_, record) => (
          <div className={`font-semibold ${record.totalDiscountAmount > 0 ? 'text-error' : 'text-primaryLight'}`}>
            {record.totalDiscountAmount} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Expected revenue'),
        key: 'expectedRevenue',
        render: (_, record) => (
          <div className="font-semibold text-accent">
            {record.expectedRevenue} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Average revenue per day'),
        key: 'averageRevenuePerDay',
        render: (_, record) => (
          <div className="font-semibold text-accent">
            {record.averageRevenuePerDay} {currencySymbol}/{t('day')}
          </div>
        ),
      },
      {
        title: t('Average rate per m2'),
        key: 'averageRevenuePerDay',
        render: (_, record) => (
          <div className="font-semibold text-accent">
            {record.averageRatePerSquareMeter} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('Average month rate per m2'),
        key: 'monthRatePerSquareMeter',
        render: (_, record) => (
          <div className="font-semibold text-accent">
            {record.monthRatePerSquareMeter} {currencySymbol}
          </div>
        ),
      },
    ],
    [currencySymbol, t],
  );

  const tableActions: Array<ServerTableActions<BoxRevenueStatisticsObject>> = useMemo(() => {
    const exportAction: Array<ServerTableActions<BoxRevenueStatisticsObject>> = isExportEnabled
      ? [
          {
            name: t('Export', { ns: 'common' }),
            theme: 'secondary',
            icon: <ExportIcon />,
            iconPosition: 'prev',
            onClick: () => {
              openExportModal({
                filters: transformBoxesRevenueFilters(appliedFilters),
                columns: getBoxesRevenueColumnKeys(t),
                entity: ExportEntity.BOXES_REVENUE,
              });
            },
          },
        ]
      : [];

    return [
      ...exportAction,
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ];
  }, [appliedFilters, isExportEnabled, openExportModal, setFiltersOpened, t]);

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7">{t('Statistics')}</div>
      <ServerTable
        columns={columns}
        fetchData={useGetBoxesRevenueStatisticsQuery}
        rowKey="boxId"
        tableActionsPosition="prev"
        tableActions={tableActions}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        defaultFilters={transformBoxesRevenueFilters(appliedFilters)}
        search
      />
      <ExportModal />
      <BoxesRevenueFilter />
    </div>
  );
});
